import i18next from 'i18next';

/**
 * Languages with locale packs (Sky X ML Excel — all sheet languages + en).
 * `zh` = Simplified Chinese, `zh-TW` = Traditional Chinese.
 */
export const SUPPORTED_LANGUAGES = [
  'en',
  'es',
  'de',
  'fr',
  'it',
  'el',
  'bg',
  'da',
  'fi',
  'id',
  'ja',
  'ko',
  'lv',
  'lt',
  'ms',
  'nb',
  'pl',
  'pt',
  'ro',
  'ru',
  'sr',
  'sv',
  'th',
  'tr',
  'vi',
  'zh',
  'zh-TW',
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

const SUPPORTED_SET = new Set<string>(SUPPORTED_LANGUAGES);

/** Normalize raw tags like `zh-Hant`, `zh_CN`, `nb-NO` to a supported code. */
export function normalizeSupportedLanguage(
  raw: string | null | undefined
): SupportedLanguage | null {
  if (!raw) return null;
  const lower = raw.trim().toLowerCase().replace(/_/g, '-');
  if (!lower) return null;

  if (
    lower === 'zh-tw' ||
    lower === 'zh-hant' ||
    lower.startsWith('zh-hant') ||
    lower === 'zh-hk' ||
    lower === 'zh-mo'
  ) {
    return 'zh-TW';
  }
  if (
    lower === 'zh' ||
    lower === 'zh-cn' ||
    lower === 'zh-hans' ||
    lower.startsWith('zh-hans') ||
    lower === 'zh-sg'
  ) {
    return 'zh';
  }
  if (
    lower === 'nb' ||
    lower === 'nn' ||
    lower.startsWith('nb-') ||
    lower.startsWith('nn-')
  ) {
    return 'nb';
  }

  if (SUPPORTED_SET.has(lower)) {
    return lower as SupportedLanguage;
  }

  const base = lower.split('-')[0];
  if (SUPPORTED_SET.has(base)) {
    return base as SupportedLanguage;
  }
  return null;
}

export function getLanguage(): SupportedLanguage {
  return normalizeSupportedLanguage(i18next.language) ?? DEFAULT_LANGUAGE;
}
